/**
 * Dashboard Card Section Component
 * 
 * Renders a card-based section from dashboard API response.
 * Generic and reusable - uses columns/rows, no hardcoded field names.
 * 
 * Features:
 * - One card per warehouse (matching details page stock levels card design)
 * - Warehouse column becomes card header (if present)
 * - Stock metrics with icons and utilization bar
 * - Shopify-style design matching details page
 * - Enhanced with contextual insights, visual hierarchy, and Apple-style design
 */

import type { DashboardTableColumn } from "@/lib/erp/docs";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Box, Package, Clock, Warehouse, ArrowRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { StockActionsModal } from "./StockActionsModal";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface DashboardCardSectionProps {
  label: string;
  columns: DashboardTableColumn[];
  rows: Record<string, any>[];
  itemCode?: string;
  warehouseCount?: number;
}

function formatNumber(value: any): string {
  if (value === null || value === undefined) {
    return '-';
  }
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return String(value);
  }
  
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

// Calculate stock insights for a warehouse row
// IMPORTANT: incoming_qty comes from backend (Bin.ordered_qty) - do NOT derive it
function calculateWarehouseInsights(row: Record<string, any>, totalStockAcrossAll?: number) {
  const actual = parseFloat(row.actual_qty || 0);
  const reserved = parseFloat(row.reserved_qty || 0);
  // Backend provides incoming_qty (from Bin.ordered_qty) - use it directly, never derive
  const incoming = parseFloat(row.incoming_qty || row.ordered_qty || 0);
  
  const available = Math.max(0, actual - reserved);
  const total = reserved + actual + incoming;
  
  // Utilization: percentage of actual stock that's reserved
  const utilizationPercentage = actual > 0 
    ? Math.min(100, Math.round((reserved / actual) * 100))
    : 0;
  
  // Percentage of total stock across all warehouses
  const percentageOfTotal = totalStockAcrossAll && totalStockAcrossAll > 0
    ? Math.round((total / totalStockAcrossAll) * 100)
    : 0;
  
  // Health status: compare available stock
  let healthStatus: 'healthy' | 'low' | 'critical' = 'healthy';
  if (actual > 0) {
    const availablePercentage = (available / actual) * 100;
    if (available === 0 || availablePercentage < 10) {
      healthStatus = 'critical';
    } else if (availablePercentage < 30) {
      healthStatus = 'low';
    }
  } else if (available === 0 && incoming === 0) {
    healthStatus = 'critical';
  }
  
  return {
    actual,
    reserved,
    available,
    incoming,
    total,
    utilizationPercentage,
    percentageOfTotal,
    healthStatus,
  };
}

export function DashboardCardSection({ label, columns, rows, itemCode, warehouseCount = 0 }: DashboardCardSectionProps) {
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      noDataAvailable: t("no_data_available", "No data available"),
      noWarehousesTitle: t("no_warehouses_title", "No stock locations yet"),
      noWarehousesMessage: t("no_warehouses_message", "Add stock locations through the Inventory tab in the experience view to track stock levels."),
      addStockLocations: t("add_stock_locations", "Add Stock Locations"),
      warehouseTemplate: t("warehouse_n", "Warehouse {n}"),
      parentLabel: t("parent_label", "Parent:"),
      healthy: t("healthy", "Healthy"),
      low: t("low", "Low"),
      critical: t("critical", "Critical"),
      stockBalance: t("stock_balance", "Stock Balance"),
      totalLabel: t("total_label", "Total:"),
      reserved: t("reserved", "Reserved"),
      available: t("available", "Available"),
      incoming: t("incoming", "Incoming"),
      actual: t("actual", "Actual"),
      ofTotal: t("of_total", "of total"),
      ofActual: t("of_actual", "of actual"),
    };
  }, [language]);

  // State for Stock Actions modal
  const [selectedWarehouse, setSelectedWarehouse] = useState<{
    warehouseName: string;
    itemCode: string;
  } | null>(null);
  const [isStockActionsModalOpen, setIsStockActionsModalOpen] = useState(false);

  const rowsSafe = rows ?? [];

  // Calculate total stock across all warehouses for percentage context
  const totalStockAcrossAll = useMemo(() => {
    return rowsSafe.reduce((sum, row) => {
      const actual = parseFloat(row.actual_qty || 0);
      const reserved = parseFloat(row.reserved_qty || 0);
      const projected = parseFloat(row.projected_qty || 0);
      return sum + reserved + actual + projected;
    }, 0);
  }, [rowsSafe]);

  if (!columns || columns.length === 0) {
    return null;
  }
  
  if (rowsSafe.length === 0) {
    const showHelpfulEmptyState = warehouseCount === 0 && itemCode;
    
    return (
      <>
        <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
          <CardContent className="p-8 sm:p-10">
            {label && (
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                {label}
              </span>
            )}
            <div className="mt-3">
              {showHelpfulEmptyState ? (
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="text-sm font-medium text-foreground">
                    {ui.noWarehousesTitle}
                  </div>
                  <div className="text-xs text-muted-foreground max-w-md">
                    {ui.noWarehousesMessage}
                  </div>
                  {itemCode && (
                    <button
                      onClick={() => {
                        setSelectedWarehouse({
                          warehouseName: '',
                          itemCode: itemCode,
                        });
                        setIsStockActionsModalOpen(true);
                      }}
                      className="mt-2 px-4 py-2 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-sm transition-colors"
                    >
                      {ui.addStockLocations}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center">{ui.noDataAvailable}</p>
              )}
            </div>
          </CardContent>
        </Card>
        {itemCode && (
          <StockActionsModal
            open={isStockActionsModalOpen}
            onOpenChange={setIsStockActionsModalOpen}
            itemCode={itemCode}
            lockWarehouses={false}
          />
        )}
      </>
    );
  }
  
  // Find warehouse column (if present) - becomes card header
  const warehouseCol = columns.find(c => c.id === 'warehouse');
  
  return (
    <div className="space-y-6">
      {rowsSafe.map((row, index) => {
        // Card header: warehouse column value, or fallback
        const warehouseName = warehouseCol
          ? row[warehouseCol.id] || '-'
          : (columns[0]
              ? String(row[columns[0].id] || '-')
              : ui.warehouseTemplate.replace("{n}", String(index + 1)));
        
        // Calculate insights for this warehouse
        const insights = calculateWarehouseInsights(row, totalStockAcrossAll);
        
        return (
          <Card 
            key={index}
            className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => {
              // Extract item_code from the row (should be available in stock_levels data)
              const itemCode = row.item_code || '';
              setSelectedWarehouse({
                warehouseName: String(warehouseName),
                itemCode: itemCode,
              });
              setIsStockActionsModalOpen(true);
            }}
          >
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col space-y-6">
                {/* Header Row: Warehouse Name + Health Status + Stock Balance */}
                <div className="flex items-start justify-between gap-6">
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    {/* Parent Warehouse (if exists) - Compact, same row */}
                    {row.parent_warehouse && (
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[9px] font-medium text-muted-foreground/60 uppercase tracking-wider">
                          {ui.parentLabel}
                        </span>
                        <span className="text-[10px] font-normal text-muted-foreground/70">
                          {String(row.parent_warehouse)}
                        </span>
                      </div>
                    )}
                    
                    {/* Arrow down icon (if parent exists) */}
                    {row.parent_warehouse && (
                      <div className="flex items-center justify-start -my-1">
                        <ChevronDown className="h-2.5 w-2.5 text-muted-foreground/40" />
                      </div>
                    )}
                    
                    {/* Warehouse Name */}
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <Warehouse className="h-4 w-4 text-muted-foreground/50" />
                      <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
                        {String(warehouseName)}
                      </span>
                      {insights.percentageOfTotal > 0 && (
                        <span className="text-[10px] font-normal text-muted-foreground/60">
                          ({insights.percentageOfTotal}% {ui.ofTotal})
                        </span>
                      )}
                      {/* Stock Health Indicator - Minimal, subtle */}
                      {insights.healthStatus === 'healthy' && (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/8 border border-green-500/12">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500/80" />
                          <span className="text-[9px] font-normal text-green-600/80 dark:text-green-500/80">{ui.healthy}</span>
                        </div>
                      )}
                      {insights.healthStatus === 'low' && (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/8 border border-yellow-500/12">
                          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500/80" />
                          <span className="text-[9px] font-normal text-yellow-600/80 dark:text-yellow-500/80">{ui.low}</span>
                        </div>
                      )}
                      {insights.healthStatus === 'critical' && (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/8 border border-red-500/12">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-500/80" />
                          <span className="text-[9px] font-normal text-red-600/80 dark:text-red-500/80">{ui.critical}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right side: Stock Balance */}
                  <div className="flex flex-col items-end gap-2.5 flex-shrink-0 min-w-[40%]">
                    {/* Stock Balance Chart - Larger, top right with data */}
                    {(() => {
                      const total = insights.total;
                      if (total === 0) return null;
                      
                      const reservedPct = (insights.reserved / total) * 100;
                      const rightPct = ((insights.actual + insights.incoming) / total) * 100;
                      const actualPctOfRight = (insights.actual + insights.incoming) > 0 
                        ? (insights.actual / (insights.actual + insights.incoming)) * 100 
                        : 0;
                      const incomingPctOfRight = (insights.actual + insights.incoming) > 0 
                        ? (insights.incoming / (insights.actual + insights.incoming)) * 100 
                        : 0;
                      
                      return (
                        <div className="flex flex-col items-end gap-2.5 w-full">
                          <div className="flex items-center justify-between w-full">
                            <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
                              {ui.stockBalance}
                            </span>
                            <span className="text-[9px] font-normal text-muted-foreground/60">
                              {ui.totalLabel} {formatNumber(total)}
                            </span>
                          </div>
                          {/* Apple-style horizontal segmented bar */}
                          <div className="relative h-3 w-full rounded-full bg-muted/60 dark:bg-muted/40 overflow-hidden">
                            {/* Left: Reserved */}
                            {reservedPct > 0 && (
                              <div
                                className="absolute left-0 inset-y-0 bg-amber-500/90 dark:bg-amber-600/90 transition-all duration-300"
                                style={{ width: `${reservedPct}%` }}
                              />
                            )}
                            {/* Right: Actual + Incoming */}
                            {rightPct > 0 && (
                              <div
                                className="absolute right-0 inset-y-0 flex overflow-hidden"
                                style={{ width: `${rightPct}%` }}
                              >
                                {actualPctOfRight > 0 && (
                                  <div
                                    className="bg-emerald-500/90 dark:bg-emerald-600/90 transition-all duration-300"
                                    style={{ width: `${actualPctOfRight}%` }}
                                  />
                                )}
                                {incomingPctOfRight > 0 && (
                                  <div
                                    className="bg-sky-500/90 dark:bg-sky-600/90 transition-all duration-300"
                                    style={{ width: `${incomingPctOfRight}%` }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          {/* Legend with data values - Apple style */}
                          <div className="flex items-center gap-3 text-[9px] font-normal text-muted-foreground/70 flex-wrap justify-end">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full bg-amber-500/90 dark:bg-amber-600/90" />
                              <span className="font-medium">{ui.reserved}</span>
                              <span className="tabular-nums">{formatNumber(insights.reserved)}</span>
                            </div>
                            <ArrowRight className="h-2.5 w-2.5 text-muted-foreground/30" />
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full bg-emerald-500/90 dark:bg-emerald-600/90" />
                              <span className="font-medium">{ui.available}</span>
                              <span className="tabular-nums">{formatNumber(insights.available)}</span>
                            </div>
                            <ArrowRight className="h-2.5 w-2.5 text-muted-foreground/30" />
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full bg-sky-500/90 dark:bg-sky-600/90" />
                              <span className="font-medium">{ui.incoming}</span>
                              <span className="tabular-nums">{formatNumber(insights.incoming)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Stock Metrics Grid - Apple-style spacing and typography */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-2 border-t border-border/15">
                  {/* Actual Stock */}
                  <div className="flex flex-col space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Box className="h-3.5 w-3.5 text-muted-foreground/50" />
                      <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                        {ui.actual}
                      </span>
                    </div>
                    <span className="text-2xl font-semibold text-foreground leading-tight tabular-nums">
                      {formatNumber(insights.actual)}
                    </span>
                  </div>

                  {/* Reserved Stock with Utilization */}
                  <div className="flex flex-col space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Package className="h-3.5 w-3.5 text-muted-foreground/50" />
                      <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                        {ui.reserved}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-foreground leading-tight tabular-nums">
                        {formatNumber(insights.reserved)}
                      </span>
                      {insights.utilizationPercentage > 0 && (
                        <span className="text-[10px] font-normal text-amber-600/80 dark:text-amber-500/80 whitespace-nowrap">
                          {insights.utilizationPercentage}% {ui.ofActual}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Incoming Stock */}
                  <div className="flex flex-col space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground/50" />
                      <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                        {ui.incoming}
                      </span>
                    </div>
                    <span className="text-2xl font-semibold text-foreground leading-tight tabular-nums">
                      {formatNumber(insights.incoming)}
                    </span>
                  </div>

                  {/* Available Stock - Primary Metric */}
                  <div className="flex flex-col space-y-1.5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground/50" />
                      <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                        {ui.available}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={cn(
                        "text-2xl font-semibold leading-tight tabular-nums",
                        insights.available > 0 ? "text-emerald-600/90 dark:text-emerald-500/90" : "text-muted-foreground/50"
                      )}>
                        {formatNumber(insights.available)}
                      </span>
                      {insights.actual > 0 && (
                        <span className="text-[10px] text-muted-foreground/60 font-normal whitespace-nowrap">
                          {Math.round((insights.available / insights.actual) * 100)}% {ui.ofActual}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {/* Stock Actions Modal */}
      {selectedWarehouse && (
        <StockActionsModal
          open={isStockActionsModalOpen}
          onOpenChange={setIsStockActionsModalOpen}
          warehouseName={selectedWarehouse.warehouseName}
          itemCode={selectedWarehouse.itemCode}
        />
      )}
    </div>
  );
}

